import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Home, 
  Users, 
  FileText, 
  FolderOpen,
  CreditCard,
  Receipt,
  AlertTriangle,
  Bell,
  LogOut,
  ChevronRight,
  Settings,
  Download,
  Upload,
  HardDrive,
  Mail,
  Shield,
  ShieldCheck,
  LayoutDashboard
} from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const menuItems = [
  { 
    label: 'Dashboard', 
    path: '/admin', 
    icon: LayoutDashboard,
    exact: true
  },
  { 
    label: 'Immobili', 
    path: '/admin/immobili', 
    icon: Home 
  },
  { 
    label: 'Utenti', 
    path: '/admin/utenti', 
    icon: Users 
  },
  { 
    label: 'Contratti', 
    path: '/admin/contratti', 
    icon: FileText 
  },
  { 
    label: 'Pagamenti', 
    icon: CreditCard,
    children: [
      { label: 'Ordinari', path: '/admin/pagamenti', icon: CreditCard },
      { label: 'Straordinari', path: '/admin/pagamenti-straordinari', icon: Receipt },
    ]
  },
  { 
    label: 'Solleciti', 
    path: '/admin/solleciti', 
    icon: AlertTriangle 
  },
  { 
    label: 'Scadenziario', 
    path: '/admin/avvisi', 
    icon: Bell 
  },
  { 
    label: 'Email', 
    path: '/admin/email', 
    icon: Mail 
  },
];

// Strumenti Admin: sezione separata con header rosso
const adminToolsItems = [
  { 
    label: 'Template', 
    icon: FolderOpen,
    children: [
      { label: 'Documenti', path: '/admin/template-documenti', icon: FileText },
      { label: 'Testi', path: '/admin/template-testi', icon: Settings },
    ]
  },
  { 
    label: 'Centro Sicurezza', 
    path: '/admin/centro-sicurezza', 
    icon: ShieldCheck 
  },
  { 
    label: 'Registro GDPR', 
    path: '/admin/registro-gdpr', 
    icon: Shield 
  },
  { 
    label: 'Storage', 
    path: '/admin/storage', 
    icon: HardDrive 
  },
];

const AdminSidebar = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [openSubMenu, setOpenSubMenu] = useState(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Auto-open submenu if current path matches a child (sia in menuItems che adminToolsItems)
  React.useEffect(() => {
    [...menuItems, ...adminToolsItems].forEach((item) => {
      if (item.children) {
        const match = item.children.some(c => location.pathname === c.path);
        if (match) setOpenSubMenu(item.label);
      }
    });
  }, [location.pathname]);

  const renderItem = (item) => {
    if (item.children) {
      const isExpanded = openSubMenu === item.label;
      const isChildActive = item.children.some(c => location.pathname === c.path);
      return (
        <div key={item.label}>
          <button
            onClick={() => setOpenSubMenu(isExpanded ? null : item.label)}
            className={`sidebar-item w-full ${isChildActive ? 'active' : ''}`}
            data-testid={`sidebar-${item.label.toLowerCase()}`}
          >
            <item.icon className="w-5 h-5" strokeWidth={1.5} />
            <span className="flex-1 text-left">{item.label}</span>
            <ChevronRight className={`w-4 h-4 opacity-50 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
          </button>
          {isExpanded && (
            <div className="ml-4 border-l border-slate-700 pl-2 space-y-0.5 py-0.5">
              {item.children.map((child) => (
                <NavLink
                  key={child.path}
                  to={child.path}
                  className={({ isActive }) => `sidebar-item text-sm ${isActive ? 'active' : ''}`}
                  data-testid={`sidebar-${child.label.toLowerCase()}`}
                >
                  <child.icon className="w-4 h-4" strokeWidth={1.5} />
                  <span className="flex-1">{child.label}</span>
                </NavLink>
              ))}
            </div>
          )}
        </div>
      );
    }
    return (
      <NavLink
        key={item.path}
        to={item.path}
        end={item.exact}
        className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
        data-testid={`sidebar-${item.label.toLowerCase().replace(/ /g, '-')}`}
      >
        <item.icon className="w-5 h-5" strokeWidth={1.5} />
        <span className="flex-1">{item.label}</span>
        <ChevronRight className="w-4 h-4 opacity-50" />
      </NavLink>
    );
  };

  return (
    <aside className={`sidebar ${isOpen ? 'open' : ''}`} data-testid="admin-sidebar">
      {/* Header */}
      <div className="sidebar-header">
        <NavLink
          to="/admin"
          end
          className="flex items-center justify-center py-7 px-1 hover:opacity-80 transition-opacity"
          data-testid="sidebar-logo"
          title="Vai alla Dashboard"
        >
          <img src="/logo-white-cropped.svg" alt="SemplificAffitto" className="w-full" />
        </NavLink>
      </div>

      {/* Navigation */}
      <ScrollArea className="sidebar-nav flex-1">
        <nav className="space-y-0.5">
          {menuItems.map(renderItem)}

          {/* Divider + Strumenti Admin */}
          <div className="pt-3 mt-3 border-t border-slate-700">
            <p
              className="px-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-red-400 flex items-center gap-1.5"
              data-testid="sidebar-section-strumenti-admin"
            >
              <ShieldCheck className="w-3 h-3" strokeWidth={2} />
              Strumenti Admin
            </p>
            {adminToolsItems.map(renderItem)}
          </div>
        </nav>
      </ScrollArea>

      {/* Footer - Compatto */}
      <div className="sidebar-footer">
        <div className="px-3 py-2 border-t border-slate-700">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shrink-0">
              <span className="text-white text-xs font-bold">
                {user?.nome?.charAt(0)}{user?.cognome?.charAt(0)}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-xs font-semibold truncate">
                {user?.nome} {user?.cognome}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-red-400 hover:bg-slate-800 h-8 w-8 p-0 shrink-0"
              onClick={handleLogout}
              data-testid="logout-btn"
              title="Esci"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="flex-1 text-[10px] text-slate-500 hover:text-white hover:bg-slate-800 h-6"
              data-testid="backup-btn"
              onClick={async () => {
                try {
                  const res = await fetch(`${API_URL}/admin/backup`, {
                    credentials: 'include'
                  });
                  if (!res.ok) throw new Error('Backup failed');
                  const blob = await res.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `semplificaffitto_backup_${new Date().toISOString().slice(0,10)}.tar.gz`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                  toast.success('Backup (BSON) scaricato con successo');
                } catch { toast.error('Errore durante il backup'); }
              }}
            >
              <Download className="w-3 h-3 mr-1" /> Backup
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="flex-1 text-[10px] text-slate-500 hover:text-white hover:bg-slate-800 h-6"
              data-testid="restore-btn"
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.tar.gz,.gz,.json';
                input.onchange = async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  if (!window.confirm('Attenzione: il restore SOVRASCRIVE tutti i dati attuali. Procedere?')) return;
                  try {
                    const formData = new FormData();
                    formData.append('file', file);
                    const res = await axios.post(`${API_URL}/admin/restore`, formData, {
                      headers: { 'Content-Type': 'multipart/form-data' }
                    });
                    toast.success(res.data.message);
                    window.location.reload();
                  } catch { toast.error('Errore durante il restore'); }
                };
                input.click();
              }}
            >
              <Upload className="w-3 h-3 mr-1" /> Restore
            </Button>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default AdminSidebar;
